import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import * as adminService from '../services/adminService';
import { SUPER_USER_EMAIL } from '../config/constants'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    SwatchIcon, GlobeAltIcon, CpuChipIcon, CheckCircleIcon, 
    PhotoIcon, PaintBrushIcon, CircleStackIcon, PlayIcon,
    QueueListIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon, EyeSlashIcon,
    ArrowDownTrayIcon, ArrowUpTrayIcon, ExclamationTriangleIcon
} from '@heroicons/react/24/solid';

const SuperAdminPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('branding'); // branding, builder, appearance, integrations, database
    const [loading, setLoading] = useState(false);

    // --- ESTADOS ---
    const [companyName, setCompanyName] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [watermarkUrl, setWatermarkUrl] = useState('');
    
    const [primaryColor, setPrimaryColor] = useState('#2563EB');
    const [secondaryColor, setSecondaryColor] = useState('#1E40AF');
    const [accentColor, setAccentColor] = useState('#F59E0B');
    const [fontColor, setFontColor] = useState('#1F2937');
    const [iconSet, setIconSet] = useState('solid');

    // Nuevos campos SMTP para NodeMailer (Cloud Functions)
    const [smtpHost, setSmtpHost] = useState('');
    const [smtpPort, setSmtpPort] = useState(465);
    const [smtpUser, setSmtpUser] = useState('');
    const [smtpPass, setSmtpPass] = useState('');

    const [smtpHostCobranza, setSmtpHostCobranza] = useState('');
    const [smtpPortCobranza, setSmtpPortCobranza] = useState(465);
    const [smtpUserCobranza, setSmtpUserCobranza] = useState('');
    const [smtpPassCobranza, setSmtpPassCobranza] = useState('');

    const [showSmtpPass, setShowSmtpPass] = useState(false);
    const [showSmtpPassCobranza, setShowSmtpPassCobranza] = useState(false);

    // Estado del Constructor Web
    const [landingConfig, setLandingConfig] = useState([
        { id: 'hero', label: 'Bienvenida (Hero)', enabled: true },
        { id: 'services', label: 'Servicios', enabled: true },
        { id: 'about', label: 'Sobre Nosotros', enabled: true },
        { id: 'projects', label: 'Proyectos / Industrias', enabled: true },
        { id: 'contact', label: 'Contacto', enabled: true }
    ]);

    const logoInputRef = useRef(null);
    const watermarkInputRef = useRef(null);
    const restoreInputRef = useRef(null); // Ref para el input de archivo JSON

    // Cargar datos al montar
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const data = await adminService.getSystemSettings();
                if (data) {
                    setCompanyName(data.companyName || '');
                    setLogoUrl(data.companyLogoUrl || '');
                    setWatermarkUrl(data.watermarkUrl || '');
                    
                    setPrimaryColor(data.primaryColor || '#2563EB');
                    setSecondaryColor(data.secondaryColor || '#1E40AF');
                    setAccentColor(data.accentColor || '#F59E0B');
                    setFontColor(data.fontColor || '#1F2937');
                    setIconSet(data.iconSet || 'solid');

                    if (data.landingConfig) {
                        setLandingConfig(data.landingConfig);
                    }
                }
                const smtpData = await adminService.getSmtpSettings();
                if (smtpData) {
                    setSmtpHost(smtpData.smtpHost || '');
                    setSmtpPort(smtpData.smtpPort || 465);
                    setSmtpUser(smtpData.smtpUser || '');
                    setSmtpPass(smtpData.smtpPass || '');

                    setSmtpHostCobranza(smtpData.smtpHostCobranza || '');
                    setSmtpPortCobranza(smtpData.smtpPortCobranza || 465);
                    setSmtpUserCobranza(smtpData.smtpUserCobranza || '');
                    setSmtpPassCobranza(smtpData.smtpPassCobranza || '');
                }
            } catch (error) {
                setNotification({ message: 'Error cargando configuración.', type: 'error' });
            }
        };
        loadSettings();
        // eslint-disable-next-line
    }, []);

    // Funciones auxiliares UI -> Servicio
    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setLoading(true);
        try {
            const url = await adminService.uploadSystemImage(file, 'system');
            if (url) setLogoUrl(url);
            setNotification({ message: 'Logo subido. Recuerda guardar.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al subir logo.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleWatermarkChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const url = await adminService.uploadSystemImage(file, 'system');
            if (url) setWatermarkUrl(url);
            setNotification({ message: 'Marca de agua subida. Recuerda guardar.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al subir marca de agua.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Lógica del Constructor Web (Reordenar y Toggle) - Se mantiene local hasta guardar
    const moveSection = (index, direction) => {
        const newConfig = [...landingConfig];
        if (direction === 'up' && index > 0) {
            [newConfig[index], newConfig[index - 1]] = [newConfig[index - 1], newConfig[index]];
        } else if (direction === 'down' && index < newConfig.length - 1) {
            [newConfig[index], newConfig[index + 1]] = [newConfig[index + 1], newConfig[index]];
        }
        setLandingConfig(newConfig);
    };

    const toggleSection = (index) => {
        const newConfig = [...landingConfig];
        newConfig[index].enabled = !newConfig[index].enabled;
        setLandingConfig(newConfig);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const settingsData = {
                companyName,
                companyLogoUrl: logoUrl,
                watermarkUrl,
                primaryColor,
                secondaryColor,
                fontColor,
                iconSet,
                landingConfig
            };

            const smtpData = {
                smtpHost,
                smtpPort: parseInt(smtpPort) || 465,
                smtpUser,
                smtpPass,
                smtpHostCobranza,
                smtpPortCobranza: parseInt(smtpPortCobranza) || 465,
                smtpUserCobranza,
                smtpPassCobranza
            };

            await adminService.saveSystemSettings(settingsData);
            await adminService.saveSmtpSettings(smtpData);
            setNotification({ message: 'Configuración maestra y SMTP actualizadas.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al guardar configuración.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInitializeCounters = async () => {
        if (!window.confirm("Esto creará los contadores iniciales (Folios) si no existen. ¿Continuar?")) return;
        setLoading(true);
        try {
            await adminService.initializeSystemCounters();
            setNotification({ message: `Contadores inicializados y verificados.`, type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al inicializar contadores.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Manejador de Backup JSON (Descargar)
    const handleBackup = async () => {
        setLoading(true);
        try {
            const backupData = await adminService.createFullBackup();
            
            const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
            const href = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = href;
            link.download = `BantCore_Backup_${new Date().toISOString().slice(0,10)}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setNotification({ message: 'Respaldo JSON descargado correctamente.', type: 'success' });
        } catch (error) {
            console.error(error);
            setNotification({ message: 'Error generando respaldo.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // NUEVO: Manejador de Restauración (Importar)
    const handleRestore = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!window.confirm("⚠️ PELIGRO CRÍTICO ⚠️\n\nEsta acción sobrescribirá los datos actuales con los del archivo de respaldo. \n\n¿Estás 100% seguro de que quieres continuar?")) {
            restoreInputRef.current.value = ''; // Limpiar input
            return;
        }

        setLoading(true);
        const reader = new FileReader();
        
        reader.onload = async (event) => {
            try {
                const jsonData = JSON.parse(event.target.result);
                const count = await adminService.restoreSystemFromBackup(jsonData);
                setNotification({ message: `Restauración completada. ${count} registros procesados.`, type: 'success' });
            } catch (error) {
                console.error("Restore Error:", error);
                setNotification({ message: 'Error crítico al restaurar: Archivo inválido o corrupto.', type: 'error' });
            } finally {
                setLoading(false);
                restoreInputRef.current.value = ''; // Limpiar input para permitir subir el mismo archivo de nuevo si falla
            }
        };

        reader.readAsText(file);
    };

    const handleTestSmtp = async (type) => {
        setLoading(true);
        try {
            const functions = getFunctions();
            const testSmtp = httpsCallable(functions, 'testSmtpConnection');
            
            let payload;
            if (type === 'primary') {
                if (!smtpHost || !smtpUser || !smtpPass) throw new Error('Faltan datos en la cuenta principal');
                payload = { host: smtpHost, port: parseInt(smtpPort) || 587, user: smtpUser, pass: smtpPass };
            } else {
                if (!smtpHostCobranza || !smtpUserCobranza || !smtpPassCobranza) throw new Error('Faltan datos en la cuenta de cobranza');
                payload = { host: smtpHostCobranza, port: parseInt(smtpPortCobranza) || 587, user: smtpUserCobranza, pass: smtpPassCobranza };
            }
            
            await testSmtp(payload);
            setNotification({ message: '¡Conexión exitosa! Correo de prueba enviado.', type: 'success' });
        } catch (error) {
            setNotification({ message: `Error en la prueba: ${error.message}`, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Verificación de acceso
    if (user.email !== SUPER_USER_EMAIL && user.role !== 'superuser') {
       return <div className="p-8 text-center text-red-600 font-bold">Acceso Restringido: Solo Super Admin.</div>;
    }

    return (
        <div className="p-6 bg-gray-100 dark:bg-gray-900 min-h-screen transition-colors">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center mb-6">
                    <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline mr-4 transition-colors">&larr; Volver</button>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Super Admin: Marca Blanca</h1>
                </div>

                {/* TABS NAVEGACIÓN */}
                <div className="flex bg-white dark:bg-gray-800 rounded-lg shadow p-1 mb-6 overflow-x-auto transition-colors">
                    <button onClick={() => setActiveTab('branding')} className={`flex-1 py-3 px-4 rounded-md font-semibold flex justify-center items-center whitespace-nowrap transition-colors ${activeTab === 'branding' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <GlobeAltIcon className="w-5 h-5 mr-2"/> Identidad
                    </button>
                    <button onClick={() => setActiveTab('builder')} className={`flex-1 py-3 px-4 rounded-md font-semibold flex justify-center items-center whitespace-nowrap transition-colors ${activeTab === 'builder' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <QueueListIcon className="w-5 h-5 mr-2"/> Constructor Web
                    </button>
                    <button onClick={() => setActiveTab('appearance')} className={`flex-1 py-3 px-4 rounded-md font-semibold flex justify-center items-center whitespace-nowrap transition-colors ${activeTab === 'appearance' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <SwatchIcon className="w-5 h-5 mr-2"/> Apariencia
                    </button>
                    <button onClick={() => setActiveTab('integrations')} className={`flex-1 py-3 px-4 rounded-md font-semibold flex justify-center items-center whitespace-nowrap transition-colors ${activeTab === 'integrations' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <CpuChipIcon className="w-5 h-5 mr-2"/> Integraciones
                    </button>
                    <button onClick={() => setActiveTab('database')} className={`flex-1 py-3 px-4 rounded-md font-semibold flex justify-center items-center whitespace-nowrap transition-colors ${activeTab === 'database' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <CircleStackIcon className="w-5 h-5 mr-2"/> Base de Datos
                    </button>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 transition-colors">
                    
                    {/* ... (TABS BRANDING, BUILDER, APPEARANCE, INTEGRATIONS SIN CAMBIOS) ... */}
                    {activeTab === 'branding' && (
                        <div className="space-y-6 animate-fade-in transition-colors">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Nombre del Software / Empresa Cliente</label>
                                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded transition-colors" placeholder="Ej: Constructora del Sur" />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="border dark:border-gray-700 p-4 rounded-lg text-center transition-colors">
                                    <p className="font-bold text-gray-600 dark:text-gray-400 mb-2">Logo Principal</p>
                                    <div className="h-32 bg-gray-100 dark:bg-gray-900 flex items-center justify-center mb-3 rounded overflow-hidden transition-colors">
                                        {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full object-contain"/> : <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600"/>}
                                    </div>
                                    <button onClick={() => logoInputRef.current.click()} className="text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">Subir Logo</button>
                                    <input type="file" ref={logoInputRef} onChange={handleLogoChange} className="hidden" accept="image/*" />
                                </div>
                                <div className="border dark:border-gray-700 p-4 rounded-lg text-center transition-colors">
                                    <p className="font-bold text-gray-600 dark:text-gray-400 mb-2">Marca de Agua (PDFs)</p>
                                    <div className="h-32 bg-gray-100 dark:bg-gray-900 flex items-center justify-center mb-3 rounded overflow-hidden transition-colors">
                                        {watermarkUrl ? <img src={watermarkUrl} alt="Watermark" className="h-full object-contain opacity-50 dark:opacity-75"/> : <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600"/>}
                                    </div>
                                    <button onClick={() => watermarkInputRef.current.click()} className="text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">Subir Marca</button>
                                    <input type="file" ref={watermarkInputRef} onChange={handleWatermarkChange} className="hidden" accept="image/*" />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'builder' && (
                        <div className="space-y-6 animate-fade-in transition-colors">
                            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border-l-4 border-orange-400 dark:border-orange-500 transition-colors">
                                <h4 className="font-bold text-orange-800 dark:text-orange-300">Constructor de Landing Page</h4>
                                <p className="text-sm text-orange-700 dark:text-orange-400">Arrastra (usa las flechas) para reordenar las secciones de la página de inicio. Usa el ojo para ocultar secciones temporalmente.</p>
                            </div>

                            <div className="space-y-2">
                                {landingConfig.map((section, index) => (
                                    <div key={section.id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${section.enabled ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 opacity-60'}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-bold text-gray-400 dark:text-gray-500 w-6">#{index + 1}</span>
                                            <span className="font-medium text-gray-700 dark:text-gray-200">{section.label}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => toggleSection(index)} className={`p-1 rounded transition-colors ${section.enabled ? 'text-green-600 dark:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/30' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`} title={section.enabled ? "Ocultar" : "Mostrar"}>
                                                {section.enabled ? <EyeIcon className="w-5 h-5"/> : <EyeSlashIcon className="w-5 h-5"/>}
                                            </button>
                                            <div className="h-6 border-r border-gray-300 dark:border-gray-600 mx-1"></div>
                                            <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-30 transition-colors"><ArrowUpIcon className="w-5 h-5"/></button>
                                            <button onClick={() => moveSection(index, 'down')} disabled={index === landingConfig.length - 1} className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-30 transition-colors"><ArrowDownIcon className="w-5 h-5"/></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'appearance' && (
                        <div className="space-y-6 animate-fade-in transition-colors">
                            <div className="grid grid-cols-2 gap-8">
                                <div>
                                    <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center transition-colors"><PaintBrushIcon className="w-5 h-5 mr-2"/> Paleta de Colores</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between"><label className="text-sm dark:text-gray-300">Color Primario</label><input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="h-8 w-16 cursor-pointer border dark:border-gray-600 rounded bg-white dark:bg-gray-700"/></div>
                                        <div className="flex items-center justify-between"><label className="text-sm dark:text-gray-300">Color Secundario</label><input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="h-8 w-16 cursor-pointer border dark:border-gray-600 rounded bg-white dark:bg-gray-700"/></div>
                                        <div className="flex items-center justify-between"><label className="text-sm dark:text-gray-300">Color Acento</label><input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="h-8 w-16 cursor-pointer border dark:border-gray-600 rounded bg-white dark:bg-gray-700"/></div>
                                        <div className="flex items-center justify-between"><label className="text-sm dark:text-gray-300">Color de Texto</label><input type="color" value={fontColor} onChange={e => setFontColor(e.target.value)} className="h-8 w-16 cursor-pointer border dark:border-gray-600 rounded bg-white dark:bg-gray-700"/></div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-4 transition-colors">Set de Iconos</h3>
                                    <div className="space-y-2">
                                        {['solid', 'outline', 'mini'].map(set => (
                                            <label key={set} className={`flex items-center p-3 border dark:border-gray-700 rounded-lg cursor-pointer transition-colors ${iconSet === set ? 'border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                                                <input type="radio" name="iconset" value={set} checked={iconSet === set} onChange={e => setIconSet(e.target.value)} className="mr-3 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600"/>
                                                <span className="capitalize text-gray-800 dark:text-gray-200">{set} Style</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'integrations' && (
                        <div className="space-y-6 animate-fade-in transition-colors">
                            <h3 className="text-xl font-bold dark:text-gray-100 border-b dark:border-gray-700 pb-2">Servidores de Correo (SMTP)</h3>
                            
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-400 dark:border-blue-500 mb-4 transition-colors">
                                <p className="text-sm text-blue-800 dark:text-blue-300"><strong>Cuenta Principal:</strong> Se usará por defecto para cotizaciones, informes técnicos y notificaciones.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Servidor / Host SMTP</label><input type="text" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="Ej: mail.bant.cl" className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm transition-colors"/></div>
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Puerto (Ej: 465 SSL, 587 TLS)</label><input type="number" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm transition-colors"/></div>
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Usuario (Correo de envío)</label><input type="email" value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="Ej: info@bant.cl" className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm transition-colors"/></div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contraseña</label>
                                    <div className="relative mt-1">
                                        <input type={showSmtpPass ? "text" : "password"} value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder="••••••••••••" className="w-full border dark:border-gray-600 p-2 pr-10 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm transition-colors"/>
                                        <button type="button" onClick={() => setShowSmtpPass(!showSmtpPass)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-blue-500">
                                            {showSmtpPass ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button onClick={() => handleTestSmtp('primary')} disabled={loading} className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-4 py-2 rounded font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors flex items-center">
                                    <PlayIcon className="w-4 h-4 mr-2"/> Enviar Correo de Prueba (Principal)
                                </button>
                            </div>

                            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-400 dark:border-purple-500 mb-4 mt-8 transition-colors">
                                <p className="text-sm text-purple-800 dark:text-purple-300"><strong>Cuenta de Cobranza (Opcional):</strong> Si configuras esto, los correos de facturación o cobro saldrán por este servidor en lugar del principal.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Servidor / Host SMTP (Cobranza)</label><input type="text" value={smtpHostCobranza} onChange={e => setSmtpHostCobranza(e.target.value)} placeholder="Ej: mail.bant.cl" className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm transition-colors"/></div>
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Puerto (Ej: 465 SSL, 587 TLS)</label><input type="number" value={smtpPortCobranza} onChange={e => setSmtpPortCobranza(e.target.value)} className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm transition-colors"/></div>
                                <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Usuario (Ej: cobranzas@bant.cl)</label><input type="email" value={smtpUserCobranza} onChange={e => setSmtpUserCobranza(e.target.value)} placeholder="Ej: cobranzas@bant.cl" className="mt-1 w-full border dark:border-gray-600 p-2 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm transition-colors"/></div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contraseña</label>
                                    <div className="relative mt-1">
                                        <input type={showSmtpPassCobranza ? "text" : "password"} value={smtpPassCobranza} onChange={e => setSmtpPassCobranza(e.target.value)} placeholder="••••••••••••" className="w-full border dark:border-gray-600 p-2 pr-10 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm transition-colors"/>
                                        <button type="button" onClick={() => setShowSmtpPassCobranza(!showSmtpPassCobranza)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-purple-500">
                                            {showSmtpPassCobranza ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button onClick={() => handleTestSmtp('cobranza')} disabled={loading} className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-4 py-2 rounded font-semibold hover:bg-purple-200 dark:hover:bg-purple-900/60 transition-colors flex items-center">
                                    <PlayIcon className="w-4 h-4 mr-2"/> Enviar Correo de Prueba (Cobranza)
                                </button>
                            </div>
                        </div>
                    )}

                    {/* TAB: BASE DE DATOS */}
                    {activeTab === 'database' && (
                        <div className="space-y-6 animate-fade-in transition-colors">
                            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border-l-4 border-red-400 dark:border-red-500 mb-4 transition-colors">
                                <h4 className="font-bold text-red-800 dark:text-red-300 mb-1 flex items-center">
                                    <ExclamationTriangleIcon className="w-5 h-5 mr-2"/> Zona de Peligro & Mantenimiento
                                </h4>
                                <p className="text-sm text-red-700 dark:text-red-400">Acciones críticas sobre la base de datos. Procede con precaución.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Inicializador */}
                                <div className="border dark:border-gray-700 p-4 rounded-lg hover:shadow-md transition-all bg-white dark:bg-gray-800">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded text-blue-600 dark:text-blue-400 transition-colors"><PlayIcon className="w-6 h-6"/></div>
                                        <h3 className="font-bold text-gray-800 dark:text-gray-200 transition-colors">Inicializar Contadores</h3>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 min-h-[40px] transition-colors">Crea los documentos de secuencia para Folios (EGR, COT, etc.). No sobrescribe si ya existen.</p>
                                    <button onClick={handleInitializeCounters} className="w-full bg-blue-600 dark:bg-blue-700 text-white py-2 rounded font-bold hover:bg-blue-700 dark:hover:bg-blue-600 text-sm transition-colors">
                                        Ejecutar Inicialización
                                    </button>
                                </div>

                                {/* Backup & Restore (JSON) */}
                                <div className="border dark:border-gray-700 p-4 rounded-lg hover:shadow-md transition-all bg-gray-50 dark:bg-gray-900 border-gray-300">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded text-green-600 dark:text-green-400 transition-colors"><CircleStackIcon className="w-6 h-6"/></div>
                                        <h3 className="font-bold text-gray-800 dark:text-gray-200 transition-colors">Respaldo Completo</h3>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 min-h-[40px] transition-colors">Exporta e Importa toda la información del sistema en formato JSON. (No incluye imágenes, solo links).</p>
                                    
                                    <div className="flex gap-2">
                                        <button onClick={handleBackup} disabled={loading} className="flex-1 bg-green-600 dark:bg-green-700 text-white py-2 rounded font-bold hover:bg-green-700 dark:hover:bg-green-600 text-sm flex items-center justify-center gap-2 shadow-sm transition-colors">
                                            <ArrowDownTrayIcon className="w-4 h-4"/> Exportar
                                        </button>
                                        
                                        {/* Input oculto para subir archivo */}
                                        <input 
                                            type="file" 
                                            ref={restoreInputRef} 
                                            onChange={handleRestore} 
                                            accept=".json" 
                                            className="hidden" 
                                        />
                                        
                                        <button 
                                            onClick={() => restoreInputRef.current.click()} 
                                            disabled={loading} 
                                            className="flex-1 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 py-2 rounded font-bold hover:bg-red-50 dark:hover:bg-red-900/20 text-sm flex items-center justify-center gap-2 shadow-sm transition-colors"
                                            title="Cargar un archivo JSON para restaurar"
                                        >
                                            <ArrowUpTrayIcon className="w-4 h-4"/> Importar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-8 flex justify-end">
                        <button onClick={handleSave} disabled={loading} className="bg-green-600 dark:bg-green-700 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 dark:hover:bg-green-600 shadow-lg transition transform hover:-translate-y-0.5 flex items-center disabled:opacity-50">
                            {loading ? 'Guardando...' : <><CheckCircleIcon className="w-5 h-5 mr-2"/> Guardar Configuración Maestra</>}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SuperAdminPage;